import { TokenWithBalance } from '@/domain/common/types'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { TokensInfo } from '@/domain/wallet/useTokens/TokenInfo'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { getSwapTokens } from './tokensQuery'

/**
 * Returns swap EVM tokens as a TokensInfo instance, using metadata
 * directly from the Lifi API instead of making individual RPC calls
 * for decimals/symbol/name per token.
 *
 * Balances are set to 0 for now — balance fetching will be added separately.
 */
export function useSwapEvmTokens({
  nativeAssetSymbol,
  wrappedNativeAssetSymbol,
}: {
  nativeAssetSymbol: TokenSymbol
  wrappedNativeAssetSymbol: TokenSymbol
}): { tokensInfo: TokensInfo } {
  const { data: apiTokens } = useSuspenseQuery(getSwapTokens())

  const tokensInfo = useMemo(
    () =>
      new TokensInfo(
        apiTokens
          .filter((t) => t.token.symbol !== wrappedNativeAssetSymbol && t.token.symbol !== nativeAssetSymbol)
          .map(
            (t): TokenWithBalance => ({
              token: new Token({
                symbol: t.token.symbol,
                name: t.token.name,
                decimals: t.token.decimals,
                address: CheckedAddress(t.token.address),
                unitPriceUsd: t.token.unitPriceUsd.toFixed(),
              }),
              balance: NormalizedUnitNumber(0),
            }),
          ),
      ),
    [apiTokens, nativeAssetSymbol, wrappedNativeAssetSymbol],
  )

  return { tokensInfo }
}
